import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FaArrowLeft, FaArrowUp, FaArrowDown, FaExternalLinkAlt, FaStar } from 'react-icons/fa';
import { BiTrendingUp, BiTrendingDown } from 'react-icons/bi';
import Loading from '../../components/Common/Loading';
import { useUser } from '../../context/UserContext';
import { fetchCoinDetails } from '../../utils/CoinGeckoCache';
import './styles.css';

const CoinDetails = () => {
  const { coinId } = useParams();
  const navigate = useNavigate();
  const { user } = useUser();
  const [coinData, setCoinData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [favorites, setFavorites] = useState([]);

  // Custom descriptions for specific coins
  const getCustomDescription = (coinId) => {
    const customDescriptions = {
      'steem': 'Steem is a community-driven blockchain that offers users instant earning opportunities. Its protocol rewards participants based on the value they bring to the network. By providing a platform for sharing and curating content, Steem enables users to publish online posts and earn cryptocurrency in return.',
      'puss': 'PussFi (PUSS) is a decentralized memecoin uniquely launched on the Tron blockchain through the SunPump platform. Setting itself apart from typical cryptocurrencies, PussFi combines utility with strong community engagement, highlighted by its integration with Steemit, a leading blockchain-based social media platform. This connection gives users extra earning opportunities, making PUSS attractive for more than just speculation.'
    };
    
    return customDescriptions[coinId] || null;
  };

  // Load favorites from localStorage only when user is logged in
  useEffect(() => {
    if (user) {
      const savedFavorites = localStorage.getItem('cryptoFavorites');
      if (savedFavorites) {
        setFavorites(JSON.parse(savedFavorites));
      }
    } else {
      setFavorites([]);
    }
  }, [user]);

  // Save favorites to localStorage only when user is logged in
  const saveFavorites = (newFavorites) => {
    if (!user) return;
    
    setFavorites(newFavorites);
    localStorage.setItem('cryptoFavorites', JSON.stringify(newFavorites));
  };

  // Toggle favorite
  const toggleFavorite = (id) => {
    if (!user) {
      alert('Please login to add coins to your favorites!');
      return;
    }
    
    const newFavorites = favorites.includes(id)
      ? favorites.filter(favId => favId !== id)
      : [...favorites, id];
    saveFavorites(newFavorites);
  };

  // Fetch coin details with caching
  useEffect(() => {
    const fetchData = async () => {
      if (!coinId) return;
      
      setLoading(true);
      setError(null);
      
      try {
        const result = await fetchCoinDetails(coinId);
        setCoinData(result.data);
      } catch (err) {
        setError('Failed to load cryptocurrency data. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [coinId]);

  // Format large numbers
  const formatNumber = (num) => {
    if (!num) return 'N/A';
    if (num >= 1e12) return `$${(num / 1e12).toFixed(2)}T`;
    if (num >= 1e9) return `$${(num / 1e9).toFixed(2)}B`;
    if (num >= 1e6) return `$${(num / 1e6).toFixed(2)}M`;
    if (num >= 1e3) return `$${(num / 1e3).toFixed(2)}K`;
    return `$${num.toFixed(2)}`;
  };

  // Format price
  const formatPrice = (price) => {
    if (!price) return 'N/A';
    if (price < 0.01) return `$${price.toFixed(6)}`;
    if (price < 1) return `$${price.toFixed(4)}`;
    return `$${price.toFixed(2)}`;
  };

  // Format percentage
  const formatPercentage = (change) => {
    if (change === null || change === undefined) return 'N/A';
    const formatted = change.toFixed(2);
    return change >= 0 ? `+${formatted}%` : `${formatted}%`;
  };

  // Format supply in millions/billions for better readability
  const formatSupply = (num) => {
    if (!num) return 'N/A';
    if (num >= 1e12) return `${(num / 1e12).toFixed(2)}T`;
    if (num >= 1e9) return `${(num / 1e9).toFixed(2)}B`;
    if (num >= 1e6) return `${(num / 1e6).toFixed(2)}M`;
    if (num >= 1e3) return `${(num / 1e3).toFixed(2)}K`;
    return num.toLocaleString();
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  if (loading) {
    return (
      <div className="coin-details-container">
        <Loading />
      </div>
    );
  }

  if (error) {
    return (
      <div className="coin-details-container">
        <div className="error-section">
          <button className="back-button" onClick={() => navigate('/')}>
            <FaArrowLeft /> Back to Home
          </button>
          <div className="error-message">
            <h2>Error Loading Cryptocurrency</h2>
            <p>{error}</p>
            <button 
              className="retry-button"
              onClick={() => window.location.reload()}
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!coinData) {
    return (
      <div className="coin-details-container">
        <div className="error-section">
          <button className="back-button" onClick={() => navigate('/')}>
            <FaArrowLeft /> Back to Home
          </button>
          <div className="error-message">
            <h2>Cryptocurrency Not Found</h2>
            <p>The requested cryptocurrency could not be found.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="coin-details-container">
      {/* Header */}
      <div className="coin-details-header">
        <button className="back-button" onClick={() => navigate('/')}>
          <FaArrowLeft /> Back to Home
        </button>
        <button
          className={`favorite-btn-large ${favorites.includes(coinData.id) ? 'favorited' : ''} ${!user ? 'disabled' : ''}`}
          onClick={() => toggleFavorite(coinData.id)}
          title={!user ? 'Please login to add to favorites' : ''}
        >
          <FaStar /> {
            !user 
              ? 'Login to Add Favorites' 
              : favorites.includes(coinData.id) 
                ? 'Remove from Favorites' 
                : 'Add to Favorites'
          }
        </button>
      </div>

      {/* Main Coin Info */}
      <div className="coin-main-section">
        <div className="coin-title-section">
          <div className="coin-title-info">
            <img 
              src={coinData.image?.large} 
              alt={coinData.name} 
              className="coin-logo-large"
              onError={(e) => {
                e.target.src = '/favicon.ico';
              }}
            />
            <div className="coin-name-info">
              <h1 className="coin-name-large">{coinData.symbol?.toUpperCase()}</h1>
            </div>
          </div>
          
          <div className="price-section-large">
            <div className="current-price-large">
              {formatPrice(coinData.market_data?.current_price?.usd)}
            </div>
            <div className="price-changes-section">
              <div className={`price-change-item ${coinData.market_data?.price_change_percentage_24h >= 0 ? 'positive' : 'negative'}`}>
                {coinData.market_data?.price_change_percentage_24h >= 0 ? <FaArrowUp /> : <FaArrowDown />}
                <span>{formatPercentage(coinData.market_data?.price_change_percentage_24h)} (24h)</span>
              </div>
              <div className={`price-change-item ${coinData.market_data?.price_change_percentage_7d >= 0 ? 'positive' : 'negative'}`}>
                {coinData.market_data?.price_change_percentage_7d >= 0 ? <BiTrendingUp /> : <BiTrendingDown />}
                <span>{formatPercentage(coinData.market_data?.price_change_percentage_7d)} (7d)</span>
              </div>
              <div className={`price-change-item ${coinData.market_data?.price_change_percentage_30d >= 0 ? 'positive' : 'negative'}`}>
                {coinData.market_data?.price_change_percentage_30d >= 0 ? <BiTrendingUp /> : <BiTrendingDown />}
                <span>{formatPercentage(coinData.market_data?.price_change_percentage_30d)} (30d)</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Market Statistics */}
      <div className="market-stats-section">
        <h2>Market Statistics</h2>
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-label">Market Cap</div>
            <div className="stat-value">{formatNumber(coinData.market_data?.market_cap?.usd)}</div>
          </div>
          
          <div className="stat-card">
            <div className="stat-label">24h Trading Volume</div>
            <div className="stat-value">{formatNumber(coinData.market_data?.total_volume?.usd)}</div>
          </div>
          
          <div className="stat-card">
            <div className="stat-label">Circulating Supply</div>
            <div className="stat-value">
              {formatSupply(coinData.market_data?.circulating_supply)}
            </div>
            <div className="stat-symbol">{coinData.symbol?.toUpperCase()}</div>
          </div>
          
          <div className="stat-card">
            <div className="stat-label">Total Supply</div>
            <div className="stat-value">
              {formatSupply(coinData.market_data?.total_supply)}
            </div>
            <div className="stat-symbol">{coinData.symbol?.toUpperCase()}</div>
          </div>
          
          <div className="stat-card">
            <div className="stat-label">All-Time High</div>
            <div className="stat-value">{formatPrice(coinData.market_data?.ath?.usd)}</div>
            <div className="stat-date">{formatDate(coinData.market_data?.ath_date?.usd)}</div>
          </div>
          
          <div className="stat-card">
            <div className="stat-label">All-Time Low</div>
            <div className="stat-value">{formatPrice(coinData.market_data?.atl?.usd)}</div>
            <div className="stat-date">{formatDate(coinData.market_data?.atl_date?.usd)}</div>
          </div>
        </div>
      </div>

      {/* Price History */}
      <div className="price-history-section">
        <h2>Price History</h2>
        <div className="price-history-grid">
          <div className="price-history-item">
            <span className="period-label">1 Hour</span>
            <span className={`period-change ${coinData.market_data?.price_change_percentage_1h_in_currency?.usd >= 0 ? 'positive' : 'negative'}`}>
              {formatPercentage(coinData.market_data?.price_change_percentage_1h_in_currency?.usd)}
            </span>
          </div>
          <div className="price-history-item">
            <span className="period-label">24 Hours</span>
            <span className={`period-change ${coinData.market_data?.price_change_percentage_24h >= 0 ? 'positive' : 'negative'}`}>
              {formatPercentage(coinData.market_data?.price_change_percentage_24h)}
            </span>
          </div>
          <div className="price-history-item">
            <span className="period-label">7 Days</span>
            <span className={`period-change ${coinData.market_data?.price_change_percentage_7d >= 0 ? 'positive' : 'negative'}`}>
              {formatPercentage(coinData.market_data?.price_change_percentage_7d)}
            </span>
          </div>
          <div className="price-history-item">
            <span className="period-label">30 Days</span>
            <span className={`period-change ${coinData.market_data?.price_change_percentage_30d >= 0 ? 'positive' : 'negative'}`}>
              {formatPercentage(coinData.market_data?.price_change_percentage_30d)}
            </span>
          </div>
          <div className="price-history-item">
            <span className="period-label">1 Year</span>
            <span className={`period-change ${coinData.market_data?.price_change_percentage_1y >= 0 ? 'positive' : 'negative'}`}>
              {formatPercentage(coinData.market_data?.price_change_percentage_1y)}
            </span>
          </div>
        </div>
      </div>

      {/* Description */}
      {(getCustomDescription(coinId) || coinData.description?.en) && (
        <div className="coin-description-section">
          <h2>About {coinData.name}</h2>
          {getCustomDescription(coinId) ? (
            <div className="description-text">
              {getCustomDescription(coinId)}
            </div>
          ) : (
            <div 
              className="description-text"
              dangerouslySetInnerHTML={{ 
                __html: coinData.description.en
              }}
            />
          )}
        </div>
      )}

      {/* Links */}
      <div className="coin-links-section">
        <h2>Official Links</h2>
        <div className="links-grid">
          {coinData.links?.homepage?.[0] && (
            <a 
              href={coinData.links.homepage[0]} 
              target="_blank" 
              rel="noopener noreferrer"
              className="link-card"
            >
              <FaExternalLinkAlt />
              <span>Official Website</span>
            </a>
          )}
          {coinData.links?.blockchain_site?.[0] && (
            <a 
              href={coinData.links.blockchain_site[0]} 
              target="_blank" 
              rel="noopener noreferrer"
              className="link-card"
            >
              <FaExternalLinkAlt />
              <span>Blockchain Explorer</span>
            </a>
          )}
          {coinData.links?.repos_url?.github?.[0] && (
            <a 
              href={coinData.links.repos_url.github[0]} 
              target="_blank" 
              rel="noopener noreferrer"
              className="link-card"
            >
              <FaExternalLinkAlt />
              <span>GitHub Repository</span>
            </a>
          )}
          {coinData.links?.twitter_screen_name && (
            <a 
              href={`https://twitter.com/${coinData.links.twitter_screen_name}`} 
              target="_blank" 
              rel="noopener noreferrer"
              className="link-card"
            >
              <FaExternalLinkAlt />
              <span>Twitter</span>
            </a>
          )}
        </div>
      </div>
    </div>
  );
};

export default CoinDetails;